import {action, computed, makeObservable, observable, runInAction} from 'mobx';

import {pollAsync} from '@yourcoach/async-utils';

import type {ID, Nullable} from '../../../utils/utility-types';
import type {RemoteFileStorageService} from '../../system/remoteFileStorage/RemoteFileStorage.service';

import {logger} from '../../../utils/logger';
import {type Destructible, FlagManager} from '../../../utils/store';
import {type SaveMediaToCachedMediaDbUseCase} from '../../useCases/Media/SaveMediaToCachedMediaDb.uc';
import {FetchMediaRequest} from './FetchMediaRequest/FetchMediaRequest';

export class CachedMediaModel implements Destructible {
  id: string = '';
  private cachedSrc: Nullable<string> = null;
  private ext: Nullable<string> = null;
  private fetchRequest: FetchMediaRequest;
  private isCacheDisabled = false;
  private isInitedFlag = new FlagManager();
  private isLoadedFlag = new FlagManager();
  private originalSrc: CachedMediaModel.Src = '';

  constructor(
    private readonly remoteFileStorageService: RemoteFileStorageService,
    private readonly saveMediaToCachedMediaDbUseCase: SaveMediaToCachedMediaDbUseCase,
  ) {
    this.fetchRequest = new FetchMediaRequest(this.remoteFileStorageService);
    makeObservable<
      CachedMediaModel,
      'cachedSrc' | 'isCacheDisabled' | 'originalSrc'
    >(this, {
      cachedSrc: observable,
      id: observable,
      init: action.bound,
      isCacheDisabled: observable,
      isLoaded: computed,
      originalSrc: observable,
      src: computed,
    });
  }

  async init(props: CachedMediaModel.InitialProps) {
    if (this.shouldBlockInit(props.src)) {
      return;
    }

    this.checkShouldDisableCache(props);
    this.computeExtension(props);
    this.initProps(props);

    this.isInitedFlag.setTrue();

    return this.download();
  }

  destruct() {
    this.fetchRequest.destruct();
  }

  get isLoaded() {
    if (this.isCacheDisabled) {
      return true;
    }

    return (
      this.remoteFileStorageService.token.isReady && this.isLoadedFlag.value
    );
  }

  get src() {
    if (!this.originalSrc) {
      return null;
    }

    if (this.isCacheDisabled) {
      if (this.isMetroBundlerAsset(this.originalSrc)) {
        return this.originalSrc;
      }

      return this.remoteFileStorageService.url.sign(this.originalSrc);
    }

    return this.isLoaded ? this.cachedSrc : undefined;
  }

  toSource(src?: CachedMediaModel.Src): CachedMediaModel.Source | undefined {
    if (!src) return undefined;

    if (this.isMetroBundlerAsset(src)) return src;

    return {uri: src};
  }

  private checkShouldDisableCache({
    disableCache,
    src,
  }: CachedMediaModel.InitialProps) {
    this.isCacheDisabled =
      disableCache ||
      this.isMetroBundlerAsset(src) ||
      this.isWebpackAsset(src) ||
      this.isMobileAsset(src) ||
      this.isBlobUrl(src);
  }

  private computeExtension(props: CachedMediaModel.InitialProps) {
    if (this.isCacheDisabled || this.isMetroBundlerAsset(props.src)) {
      return;
    }

    if (!props.src) {
      return props.ext;
    }

    this.ext =
      props.ext ??
      this.remoteFileStorageService.url.computeExtension(props.src);
  }

  private async download() {
    if (
      this.originalSrc &&
      (this.isCacheDisabled || this.isMetroBundlerAsset(this.originalSrc))
    ) {
      return;
    }

    await pollAsync(() => this.remoteFileStorageService.token.isReady);

    try {
      const cachedSrc = await this.saveMediaToCachedMediaDbUseCase.exec(
        this.originalSrc as string,
        this.fetchRequest,
        this.ext,
      );

      runInAction(() => {
        this.cachedSrc = cachedSrc;
        this.isLoadedFlag.setTrue();
      });
    } catch (e) {
      logger.error(e);
    }
  }

  private initProps(props: CachedMediaModel.InitialProps) {
    this.originalSrc = props.src;

    this.id = props.id;
  }

  private isBlobUrl(src: CachedMediaModel.Src) {
    return typeof src === 'string' && src.startsWith('blob:');
  }

  private isMetroBundlerAsset(src: CachedMediaModel.Src) {
    return typeof src === 'number';
  }

  private isMobileAsset(src: CachedMediaModel.Src) {
    const starts = ['file://', '/', 'content://', 'asset://'];

    return starts.some(start => src?.toString().startsWith(start) ?? false);
  }

  private isWebpackAsset(src: CachedMediaModel.Src) {
    if (!src) return false;

    const str = src.toString();

    // For development (relative paths)
    if (str.startsWith('/assets/')) {
      return /^\/assets\/(?:resources\/)?[a-f0-9]{8,20}\.[a-zA-Z0-9]+(?:\?.*)?$/.test(
        str,
      );
    }

    // For production (full URLs)
    return /^https?:\/\/[^/]+\/assets\/(?:resources\/)?[a-f0-9]{8,20}\.[a-zA-Z0-9]+(?:\?.*)?$/.test(
      str,
    );
  }

  private shouldBlockInit(src: CachedMediaModel.Src) {
    return (
      this.isInitedFlag.isTrue &&
      this.originalSrc &&
      src &&
      this.remoteFileStorageService.url.getClean(
        this.originalSrc.toString(),
      ) === this.remoteFileStorageService.url.getClean(src.toString())
    );
  }
}

export namespace CachedMediaModel {
  /**
   * Properties required to initialize a CachedMediaModel
   * @property  [disableCache] - Whether to bypass caching for this media
   * @property  [ext] - Optional file extension override
   * @property  id - Unique identifier for the media
   * @property  src - Source URL or asset identifier
   */
  export type InitialProps = {
    disableCache?: boolean;
    ext?: string;
    id: ID;
    src: Src;
  };

  /**
   * Unified type for  sources in RN, supporting both source obj({uri:string}) and local resources (number)
   */
  export type Source = number | {uri: string};

  /**
   * Unified type for media src, supporting both remote URLs and local resources (number RN)
   */
  export type Src = Nullable<number | string>;
}
